/**
 * Compact parliamentary/government activity list for the chart's click-a-day
 * ELITE drill — the per-item provenance behind the elite volume line, the
 * counterpart to ArticleList (media) and YouTubeVideoList (public). Like the
 * public drill, these rows already exist in salienceObservations (they ARE
 * what the volume counts), so it's a plain table read. Each row shows the
 * source it came from (Hansard / EDM / WPQ / Petition / GOV.UK), links out,
 * and names the member behind it where the source has one; a source tally
 * rides above to answer "what kind of activity was this?".
 */
import { ExternalLink } from "lucide-react";
import type { SalienceObservation } from "../../lib/api";
import { shortDate } from "./format";

export const ELITE_SOURCE_LABELS: Record<string, string> = {
  hansard: "Hansard",
  edm: "EDM",
  wpq: "WPQ",
  petition: "Petition",
  govuk: "GOV.UK",
};

function sourceTally(items: SalienceObservation[]): [string, number][] {
  const by = new Map<string, number>();
  for (const it of items) by.set(it.source, (by.get(it.source) ?? 0) + 1);
  return [...by.entries()].sort((a, b) => b[1] - a[1]);
}

export function EliteItemList({ items }: { items: SalienceObservation[] }) {
  const tally = sourceTally(items);

  return (
    <div className="space-y-2">
      {tally.length > 1 && (
        <div className="flex flex-wrap gap-1">
          {tally.map(([source, count]) => (
            <span
              key={source}
              className="inline-flex items-center gap-1 rounded bg-white/5 px-1.5 py-0.5 text-[10px] text-terminal-ink-muted"
            >
              {ELITE_SOURCE_LABELS[source] ?? source}
              <span className="tabular-nums opacity-70">×{count}</span>
            </span>
          ))}
        </div>
      )}
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li
            key={`${it.source}-${it.url || i}`}
            className="flex items-baseline gap-1.5 text-[12px]"
          >
            <span className="shrink-0 font-mono text-[10px] tabular-nums text-terminal-ink-muted">
              {shortDate(it.observedAt)}
            </span>
            <span className="shrink-0 rounded bg-white/5 px-1 text-[10px] text-terminal-ink-muted">
              {ELITE_SOURCE_LABELS[it.source] ?? it.source}
            </span>
            <a
              href={it.url}
              target="_blank"
              rel="noreferrer"
              className="group inline-flex min-w-0 items-baseline gap-1 text-terminal-ink hover:underline"
            >
              <span className="truncate">{it.title || it.url}</span>
              <ExternalLink className="h-3 w-3 shrink-0 self-center opacity-50 group-hover:opacity-100" />
            </a>
            {it.memberName && (
              <span className="shrink-0 truncate text-[10px] text-terminal-ink-muted">
                {it.memberName}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
